import { Component, effect, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { format, isToday, isYesterday } from 'date-fns';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { MessagingSocketService } from '@app/finxone-web-frontend/app/lib/services/messaging-socket-service/messaging-socket-service.service';
import { SupportChatsService } from '@app/finxone-web-frontend/app/lib/services/support-chats-service/support-chats.service';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { isValidCSS } from '@app/finxone-web-frontend/app/lib/utils/themeing.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ActiveDevice,
  baseCssConfigStyle,
  BaseCssProperties,
  cssToNgStyle,
  ProviderClass,
  SupportMessageType,
  ThreadStatus,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { combineLatest, debounceTime, Subject, take, takeUntil } from 'rxjs';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { BaseComponent } from '../base/base.component';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormID } from '@finxone-platform/form-action';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-support-chat-widget',
  templateUrl: './support-chat-widget.component.html',
  styleUrls: ['./support-chat-widget.component.scss'],
})
export class SupportChatWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy private readonly destroy$ = new Subject<void>();

  public isLoading = false;
  public hasMore = false;
  public nextCursor: string | null = null;

  public messagesList: Array<SupportMessageType> = [];

  @ViewChild('messageContainer', { static: false }) messageContainer!: ElementRef<HTMLDivElement>;
  private scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SCROLL_DEBOUNCE_MS = 300;
  public noActiveChatTemplate = '';
  public noActiveChatTemplateFunction: HandlebarsTemplateDelegate = () => '';
  public closedChatTemplate = '';
  public closedChatTemplateFunction: HandlebarsTemplateDelegate = () => '';

  public messageForm = new FormGroup({
    message: new FormControl('', {
      nonNullable: true,
    }),
  });
  public buttonStyles = {
    'background-color': 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    'border-radius': '100%',
    width: '42px',
    height: '42px',
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    padding: '0',
  };
  public activeChatId: string | null;
  public isNewSupportChat = false;
  public welcomeMessageText: string | null = null;
  public outerCss: Record<string, string> = {};
  public activeDevice: string;
  public devicetypes = ActiveDevice;
  public btnIcon: string;
  public iconStyle: baseCssConfigStyle;

  public isWorkflow: boolean;
  private readonly pageSize = 50;
  private readonly activeChatId$ = new Subject<string | null>();
  private readonly isNewSupportChat$ = new Subject<boolean>();
  public threadStatusEnum = ThreadStatus;

  public dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  public pageConfig = inject(PageConfigurationProvider);
  private readonly supportChatsService = inject(SupportChatsService);
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly messagingSocketService = inject(MessagingSocketService);
  private readonly workflowFormService = inject(WorkflowFormsService);

  constructor() {
    super();
    this.setupReactiveContextSubscriptions();
    effect(() => {
      const activeChatId = this.supportChatsService.activeChatId();
      const isNewSupportChat = this.supportChatsService.isNewChat();
      this.activeChatId$.next(activeChatId);
      this.isNewSupportChat$.next(isNewSupportChat);
    });
    effect(() => {
      this.welcomeMessageText = this.supportChatsService.welcomeMessage();
    });
  }

  ngOnInit(): void {
    this.activeDevice = this.store.selectSnapshot(RoleState.getRole).device;
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.fetchSupportConversationMessages();
    if (this.dataIn) {
      this.assignWidgetConfig();
    }
    this.messagingSocketService.newMessageEvent$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.fetchSupportConversationMessages();
    });
  }

  public get activeChatStatus(): string | ThreadStatus | null {
    return this.supportChatsService.activeChatStatus();
  }

  private scrollToBottom(): void {
    try {
      this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  private setupReactiveContextSubscriptions() {
    combineLatest([this.activeChatId$, this.isNewSupportChat$])
      .pipe(
        debounceTime(0), // Batch synchronous emissions to prevent multiple triggers
        takeUntil(this.destroy$),
      )
      .subscribe(([activeChatId, isNewSupportChat]) => {
        this.activeChatId = activeChatId;
        this.isNewSupportChat = isNewSupportChat;

        if (isNewSupportChat) {
          this.messagesList = [];
        } else if (activeChatId) {
          this.fetchSupportConversationMessages();
        }
      });
  }

  private assignWidgetConfig() {
    this.attri = this.dataIn.attri ?? null;
    this.getWidgetStyles(this.attri);
    this.outerCss = cssToNgStyle(this.attri?.['outerCss'] ?? '');
    this.loadCustomCss(this.dataIn.attri);
    this.btnIcon = this.attri.widgetProperties?.icon ?? 'plane';
    this.noActiveChatTemplate = this.attri?.['noActiveChatTemplate'] ?? '';
    this.closedChatTemplate = this.attri?.['closedChatTemplate'] ?? 'This chat is closed';
    this.noActiveChatTemplateFunction = Handlebars.compile(this.noActiveChatTemplate);
    this.closedChatTemplateFunction = Handlebars.compile(this.closedChatTemplate);
  }

  ngOnDestroy(): void {
    this.supportChatsService.resetActiveChatId();
    this.removeCustomStyles();
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
    }
    if (this.activeChatId) {
      this.messagingSocketService.leaveThread(this.activeChatId);
    }
    if (this.messagingSocketService.socket?.connected) {
      this.messagingSocketService.disconnect();
    }
  }

  private fetchSupportConversationMessages() {
    this.isLoading = true;
    if (!this.activeChatId && !this.isWorkflow) {
      this.isLoading = false;
      return;
    }
    this.supportChatsService
      .getSupportChatMessages(this.activeChatId ?? '', this.pageSize)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.messagesList = response.result;
          this.hasMore = response.hasMore;
          this.nextCursor = response.nextCursor;
          this.isLoading = false;
          this.scrollToBottom();
        },
        error: (error) => {
          console.error('Error fetching user chats:', error);
          this.isLoading = false;
        },
      });
  }

  private getWidgetStyles(attributes: UiZoneWidgetAttributeConfig) {
    this.iconStyle = this.configService.getSpecificBaseCssStyle(attributes, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);
    this.iconStyle = {
      ...this.iconStyle,
      'margin-left': '-3px', // negative margin to align icon in center
    };
  }

  public get noActiveChatTemplateString() {
    return this.noActiveChatTemplateFunction({});
  }

  public get closedChatTemplateString() {
    return this.closedChatTemplateFunction({});
  }

  private loadCustomCss(attributes: UiZoneWidgetAttributeConfig) {
    const customCssValid = isValidCSS(attributes?.['customCss']);
    if (customCssValid) {
      this.removeCustomStyles(); // remove existing style if it exists
      // Create a style element
      const styleElement = document.createElement('style');
      styleElement.setAttribute('id', 'support-chat-widget-custom-css-variables');

      // Set the CSS text
      styleElement.textContent = attributes['customCss'];

      // Append to document head
      document.head.appendChild(styleElement);
    }
  }

  private removeCustomStyles() {
    const existingStyleElement = document.getElementById('support-chat-widget-custom-css-variables');
    if (existingStyleElement) {
      existingStyleElement.remove();
    }
  }

  public autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    // Reset height to recalculate
    textarea.style.height = 'auto';

    // Use scrollHeight to capture full content height including overflow for proper auto-resize
    const newHeight = textarea.scrollHeight;

    const maxHeight = 150; // Maximum height in pixels

    if (newHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  }

  public onEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.ctrlKey || keyboardEvent.metaKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  public sendMessage(): void {
    const message = this.messageForm.value.message ?? '';
    if (!message.length) return;
    if (this.isNewSupportChat) {
      this.createNewConversation(message);
      return;
    }
    this.sendExistingConversationMessage(message);
  }

  private addNewMessageInChat(message: SupportMessageType): void {
    this.messagesList.push(message);
  }

  private sendExistingConversationMessage(message: string): void {
    if (!this.activeChatId) {
      return;
    }
    this.supportChatsService
      .sendMessageOnExistingConversation(this.activeChatId, message)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.messageForm.reset();
          this.addNewMessageInChat(response);
        },
        error: (error) => {
          console.error('Error sending message:', error);
        },
      });
  }

  private createNewConversation(message: string): void {
    const formValues = this.workflowFormService.getFormValuesFlattened(FormID.NEW_SUPPORT_CHAT_FORM);
    this.supportChatsService
      .createNewSupportConversation({
        content: message,
        subjectId: formValues?.subject ?? null,
        accountId: formValues?.accountId ?? null,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.messageForm.reset();
          this.supportChatsService.setNewChatStatus(false);
          this.supportChatsService.setActiveChatId(response.id);
          this.addNewMessageInChat(response.messages[0]);
        },
        error: (error) => {
          console.error('Error creating new support chat:', error);
        },
      });
  }

  public get noActiveChat(): boolean {
    return this.activeChatId === null && !this.isNewSupportChat;
  }

  public shouldShowDateSeparator(index: number): boolean {
    if (index === 0) return true;
    const curr = new Date(this.messagesList[index].createdAt).toLocaleDateString();
    const prev = new Date(this.messagesList[index - 1].createdAt).toLocaleDateString();
    return curr !== prev;
  }

  /**
   * Returns a human-readable date label for chat message group separators.
   *
   * - Today:              "Today"
   * - Yesterday:          "Yesterday"
   * - Same year:          "1 June"
   * - Different year:     "1 June 2024"
   *
   */
  public getDateSeparatorLabel(input: string | Date): string {
    const currentYear = new Date().getFullYear();
    const date = typeof input === 'string' ? new Date(input) : input;
    if (Number.isNaN(date.getTime())) return '';
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    if (date.getFullYear() === currentYear) return format(date, 'd MMMM');
    return format(date, 'd MMMM yyyy');
  }

  public onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;

    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
    }

    this.scrollDebounceTimer = setTimeout(() => {
      if (scrollTop === 0 && this.hasMore) {
        this.loadPreviousMessages(element);
      }
    }, this.SCROLL_DEBOUNCE_MS);
  }

  private loadPreviousMessages(scrollContainer: HTMLElement): void {
    if (!this.activeChatId || !this.hasMore || !this.nextCursor) {
      return;
    }

    const previousScrollHeight = scrollContainer.scrollHeight;

    this.supportChatsService
      .getSupportChatMessages(this.activeChatId, this.pageSize, this.nextCursor)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.messagesList = [...response.result, ...this.messagesList];
          this.hasMore = response.hasMore;
          this.nextCursor = response.nextCursor;

          setTimeout(() => {
            const newScrollHeight = scrollContainer.scrollHeight;
            scrollContainer.scrollTop = newScrollHeight - previousScrollHeight;
          }, 0);
        },
        error: (error) => {
          console.error('Error loading previous messages:', error);
        },
      });
  }
}
